import Footer from "../../components/footer";
import Header from "../../components/header";
import ReportWrapper from "../../components/report-wrapper";
import { useLocation, useParams } from "react-router-dom";
import { LOCALE } from "../../utils/constants";

/* ============================================================
   DATA
   ============================================================ */

const SECTIONS = [
  {
    number: "01",
    name: "Pattern Recognition",
    description:
      "Measures how you detect rules and regularities inside visual sequences and matrices.",
    body: "You answered 16 of 20 correctly — one of your standout sections. Progressive and alternating rules posed little trouble, and you consistently isolated the changing element to predict the next step. Your errors clustered on the densest matrices rather than any single rule type.",
    score: 16,
    total: 20,
    percent: 80,
    color: { text: "text-sky-300", bg: "bg-sky-300", dot: "bg-sky-400" },
    strength:
      "You quickly spotted progressive and alternating rules across the matrices.",
    improve:
      "Most misses came from grids that changed on two dimensions at once — isolate one variable at a time.",
    illustration: <MatrixIllustration />,
    illustrationSide: "right",
  },
  {
    number: "02",
    name: "Verbal Reasoning",
    description:
      "Measures how you reason with words, analogies and shades of meaning.",
    body: "A solid 11 of 15. You were sharp on synonyms, antonyms and odd-one-out sorting, reading the intended sense of each word quickly. You only lost pace when an analogy asked you to chain two relationships together before answering.",
    score: 11,
    total: 15,
    percent: 73,
    color: {
      text: "text-violet-300",
      bg: "bg-violet-300",
      dot: "bg-violet-400",
    },
    strength:
      "Strong on synonym / antonym contrasts and sorting items into categories.",
    improve:
      "Multi-step analogies tripped you up — restate the first pair's relationship in plain words before solving the second.",
    illustration: <AnalogyIllustration />,
    illustrationSide: "left",
  },
  {
    number: "03",
    name: "Numerical Reasoning",
    description:
      "Measures how you spot numerical patterns and relationships in series.",
    body: "12 of 18 — your most stretched section. Doubling and simple additive series were quick, reliable wins for you. The misses gathered almost entirely around sequences whose gaps themselves change, where a second layer of pattern has to be tracked.",
    score: 12,
    total: 18,
    percent: 67,
    color: {
      text: "text-orange-400",
      bg: "bg-orange-400",
      dot: "bg-orange-400",
    },
    strength: "Comfortable and fast with doubling and simple additive series.",
    improve:
      "Series with second-order differences (where the gaps themselves change) cost the most — write the differences in a row first.",
    illustration: <GrowthCurveIllustration />,
    illustrationSide: "right",
  },
  {
    number: "04",
    name: "Spatial Reasoning",
    description:
      "Measures how you mentally rotate, reflect and manipulate shapes.",
    body: "Your strongest area at 14 of 17. You rotated and reflected figures with real fluency and reliably told genuine rotations apart from mirror images — a distinction many people miss. Only a few fold-and-wrap items slipped past.",
    score: 14,
    total: 17,
    percent: 82,
    color: {
      text: "text-emerald-300",
      bg: "bg-emerald-300",
      dot: "bg-emerald-300",
    },
    strength: (
      <>
        Excellent at tracking <Highlight>rotations</Highlight> and telling them
        apart from mirror images.
      </>
    ),
    improve:
      "Only stumbled on fold-and-wrap problems — picture the net closing one face at a time.",
    illustration: <RotationIllustration />,
    illustrationSide: "left",
  },
  {
    number: "05",
    name: "Logical Deduction",
    description:
      "Measures how you draw valid conclusions from a set of given premises.",
    body: "A dependable 11 of 15. Syllogisms and ordering puzzles were well handled, and you moved through multi-premise chains cleanly. The trap cases were the ones where the premises simply don't support a definite conclusion.",
    score: 11,
    total: 15,
    percent: 73,
    color: { text: "text-amber-300", bg: "bg-amber-300", dot: "bg-amber-300" },
    strength: "Reliable with syllogisms and ordering puzzles.",
    improve: (
      <>
        Watch the "cannot be determined" cases — when a premise only says some,
        a universal conclusion rarely follows.
      </>
    ),
    illustration: <SyllogismIllustration />,
    illustrationSide: "right",
  },
];

/* ============================================================
   PRIMITIVES (typography, bars, cards)
   ============================================================ */

function Eyebrow({ children }) {
  return (
    <p className="text-sm font-semibold tracking-widest text-orange-400 uppercase mb-3">
      {children}
    </p>
  );
}

function Title({ children, size = "lg" }) {
  const sizes = {
    sm: "text-3xl md:text-4xl",
    md: "text-4xl md:text-5xl",
    lg: "text-5xl md:text-6xl",
  };

  return (
    <h1
      className={`${sizes[size]} font-extrabold text-neutral-900 leading-[1.05] mb-6`}
    >
      {children}
    </h1>
  );
}

function Subtitle({ children }) {
  return (
    <p className="text-xl text-neutral-500 leading-relaxed max-w-2xl mb-10">
      {children}
    </p>
  );
}

function Description({ children, className = "" }) {
  return (
    <p className={`text-md text-neutral-500 leading-relaxed ${className}`}>
      {children}
    </p>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-block bg-amber-300 text-neutral-900 font-bold text-sm px-4 py-1.5 rounded-full">
      {children}
    </span>
  );
}

function Highlight({ children }) {
  return <span className="bg-blue-200/70 rounded px-0.5">{children}</span>;
}

function ScoreBar({ label, score, total, percent, barColor = "bg-sky-300" }) {
  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-sm font-bold tracking-widest text-neutral-400 uppercase">
          {label}
        </span>
        <span className="text-xl font-extrabold text-neutral-900">
          {score}/{total} · {percent}%
        </span>
      </div>
      <div className="h-3 w-full rounded-full bg-neutral-200">
        <div
          className={`h-3 rounded-full ${barColor}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function InsightCard({ tone, label, children }) {
  const tones = {
    strength: {
      bg: "bg-emerald-50",
      dot: "bg-emerald-600",
      label: "text-emerald-800",
    },
    improve: {
      bg: "bg-amber-50",
      dot: "bg-amber-500",
      label: "text-amber-800",
    },
  };
  const t = tones[tone];

  return (
    <div className={`flex-1 rounded-2xl ${t.bg} p-6`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${t.dot}`} />
        <span className={`font-bold ${t.label}`}>{label}</span>
      </div>
      <p className="text-neutral-600 leading-relaxed">{children}</p>
    </div>
  );
}

function InsightRow({ strength, improve }) {
  return (
    <div className="flex flex-col md:flex-row gap-4">
      <InsightCard tone="strength" label="Strength">
        {strength}
      </InsightCard>
      <InsightCard tone="improve" label="Where to improve">
        {improve}
      </InsightCard>
    </div>
  );
}

/* ============================================================
   HERO: IQ SCORE CARD + SECTION SCORES CARD
   ============================================================ */

function StatBox({ value, suffix, label }) {
  return (
    <div className="bg-black/25 rounded-xl px-5 py-4">
      <p className="text-2xl font-bold text-white">
        {value}
        {suffix && (
          <span className="text-base font-medium text-white/50">{suffix}</span>
        )}
      </p>
      <p className="text-xs font-semibold tracking-wide text-white/50 uppercase mt-1">
        {label}
      </p>
    </div>
  );
}

function IQCard({
  iq,
  rating,
  percentile,
  correct,
  total,
  accuracy,
  consistency,
}) {
  return (
    <div
      className="relative flex-1  rounded-3xl overflow-hidden"
      style={{
        background: "rgb(43, 36, 32)",
        color: "rgb(255, 247, 242)",
        padding: "clamp(26px, 3vw, 38px)",
        boxShadow: "rgba(43, 36, 32, 0.5) 0px 22px 40px -22px",
      }}
    >
      <div
        className="absolute rounded-full"
        style={{
          top: "-50px",
          right: "-40px",
          width: "200px",
          height: "200px",
          background: "rgba(255, 140, 107, 0.22)",
        }}
      />

      <div className="relative">
        <div className="text-[0.8rem] font-bold tracking-widest uppercase opacity-65">
          Estimated IQ
        </div>

        <div className="flex items-baseline gap-[14px] mt-1.5">
          <div
            className="font-extrabold leading-[0.9]"
            style={{
              fontFamily: "Sora, sans-serif",
              fontSize: "clamp(3.4rem, 8vw, 5rem)",
              letterSpacing: "-0.04em",
            }}
          >
            {iq}
          </div>
          <Badge>{rating}</Badge>
        </div>

        <div className="mt-[18px] text-[0.98rem] opacity-85">
          You scored in the {percentile}rd percentile — ahead of {percentile}%
          of test-takers.
        </div>

        <div className="flex flex-wrap gap-[10px] mt-[22px]">
          <StatBox value={correct} suffix={`/${total}`} label="Correct" />
          <StatBox value={`${accuracy}%`} label="Accuracy" />
          <StatBox value={`${consistency}%`} label="Consistency" />
        </div>
      </div>
    </div>
  );
}

function SectionScoreItem({ color, name, score, total, percent }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2.5">
          <span className={`w-3 h-3 rounded-full ${color.dot}`} />
          <span className="font-semibold text-neutral-800">{name}</span>
        </div>
        <span className="text-sm text-neutral-500">
          {score}/{total} · {percent}%
        </span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-neutral-100">
        <div
          className={`h-2.5 rounded-full ${color.dot}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function SectionScoresCard({ sections }) {
  return (
    <div className="flex-1 bg-white rounded-3xl p-8 shadow-sm basis-[200px]">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-extrabold text-neutral-900">
          Section scores
        </h2>
        <span className="text-sm text-neutral-400">
          {sections.length} sections
        </span>
      </div>

      <div className="flex flex-col gap-5">
        {sections.map((s) => (
          <SectionScoreItem
            key={s.name}
            color={s.color}
            name={s.name}
            score={s.score}
            total={s.total}
            percent={s.percent}
          />
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   "WHERE YOU RANK" BELL CURVE CARD
   ============================================================ */

function CardHeader({ eyebrow, title, description }) {
  return (
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <Title>{title}</Title>
      </div>
      <Description className="max-w-md md:text-right">
        {description}
      </Description>
    </div>
  );
}

function LegendItem({ children, kind }) {
  return (
    <div className="flex items-center gap-2 text-neutral-600 text-base">
      {kind === "fill" && <span className="w-4 h-4 rounded-sm bg-[#F5C4B3]" />}
      {kind === "line" && (
        <span className="w-5 h-0.5 bg-orange-400 inline-block" />
      )}
      {kind === "dashed" && (
        <span
          className="w-5 h-0.5 inline-block"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to right, #9CA3AF 0 4px, transparent 4px 8px)",
          }}
        />
      )}
      <span>{children}</span>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-6 mt-4">
      <LegendItem kind="fill">Scores below you</LegendItem>
      <LegendItem kind="line">Your score</LegendItem>
      <LegendItem kind="dashed">Population average (100)</LegendItem>
    </div>
  );
}

const BELL_CURVE_FILL =
  "M70.0,280 L70.0,277.7 L78.4,277.2 L86.9,276.6 L95.3,275.8 L103.8,275.0 L112.2,274.0 L120.7,272.9 L129.1,271.5 L137.6,270.0 L146.0,268.2 L154.4,266.2 L162.9,263.9 L171.3,261.3 L179.8,258.4 L188.2,255.2 L196.7,251.6 L205.1,247.6 L213.6,243.2 L222.0,238.4 L230.4,233.2 L238.9,227.6 L247.3,221.6 L255.8,215.2 L264.2,208.4 L272.7,201.2 L281.1,193.7 L289.6,185.9 L298.0,177.8 L306.4,169.5 L314.9,161.1 L323.3,152.6 L331.8,144.1 L340.2,135.7 L348.7,127.5 L357.1,119.5 L365.6,111.8 L374.0,104.6 L382.4,97.8 L390.9,91.7 L399.3,86.1 L407.8,81.3 L416.2,77.3 L424.7,74.2 L433.1,71.9 L441.6,70.5 L450.0,70.0 L458.4,70.5 L466.9,71.9 L475.3,74.2 L483.8,77.3 L492.2,81.3 L500.7,86.1 L509.1,91.7 L517.6,97.8 L526.0,104.6 L534.4,111.8 L542.9,119.5 L551.3,127.5 L559.8,135.7 L568.2,144.1 L576.7,152.6 L585.1,161.1 L593.6,169.5 L602.0,177.8 L610.4,185.9 L618.9,193.7 L627.3,201.2 L635.8,208.4 L644.2,215.2 L652.7,221.6 L661.1,227.6 L669.6,233.2 L678.0,238.4 L686.4,243.2 L694.9,247.6 L703.3,251.6 L711.8,255.2 L720.2,258.4 L728.7,261.3 L737.1,263.9 L745.6,266.2 L754.0,268.2 L762.4,270.0 L770.9,271.5 L779.3,272.9 L787.8,274.0 L796.2,275.0 L804.7,275.8 L813.1,276.6 L821.6,277.2 L830.0,277.7 L830.0,280 Z";

const BELL_CURVE_SHADED =
  "M70.0,280 L70.0,277.7 L78.4,277.2 L86.9,276.6 L95.3,275.8 L103.8,275.0 L112.2,274.0 L120.7,272.9 L129.1,271.5 L137.6,270.0 L146.0,268.2 L154.4,266.2 L162.9,263.9 L171.3,261.3 L179.8,258.4 L188.2,255.2 L196.7,251.6 L205.1,247.6 L213.6,243.2 L222.0,238.4 L230.4,233.2 L238.9,227.6 L247.3,221.6 L255.8,215.2 L264.2,208.4 L272.7,201.2 L281.1,193.7 L289.6,185.9 L298.0,177.8 L306.4,169.5 L314.9,161.1 L323.3,152.6 L331.8,144.1 L340.2,135.7 L348.7,127.5 L357.1,119.5 L365.6,111.8 L374.0,104.6 L382.4,97.8 L390.9,91.7 L399.3,86.1 L407.8,81.3 L416.2,77.3 L424.7,74.2 L433.1,71.9 L441.6,70.5 L450.0,70.0 L458.4,70.5 L466.9,71.9 L475.3,74.2 L483.8,77.3 L492.2,81.3 L500.7,86.1 L509.1,91.7 L517.6,97.8 L526.0,104.6 L534.4,111.8 L542.9,119.5 L551.3,127.5 L559.8,135.7 L568.2,144.1 L576.7,152.6 L585.1,161.1 L593.6,169.5 L602.0,177.8 L610.4,185.9 L618.9,193.7 L627.3,201.2 L635.8,208.4 L635.8,280 Z";

const BELL_CURVE_OUTLINE =
  "M70.0,277.7 L78.4,277.2 L86.9,276.6 L95.3,275.8 L103.8,275.0 L112.2,274.0 L120.7,272.9 L129.1,271.5 L137.6,270.0 L146.0,268.2 L154.4,266.2 L162.9,263.9 L171.3,261.3 L179.8,258.4 L188.2,255.2 L196.7,251.6 L205.1,247.6 L213.6,243.2 L222.0,238.4 L230.4,233.2 L238.9,227.6 L247.3,221.6 L255.8,215.2 L264.2,208.4 L272.7,201.2 L281.1,193.7 L289.6,185.9 L298.0,177.8 L306.4,169.5 L314.9,161.1 L323.3,152.6 L331.8,144.1 L340.2,135.7 L348.7,127.5 L357.1,119.5 L365.6,111.8 L374.0,104.6 L382.4,97.8 L390.9,91.7 L399.3,86.1 L407.8,81.3 L416.2,77.3 L424.7,74.2 L433.1,71.9 L441.6,70.5 L450.0,70.0 L458.4,70.5 L466.9,71.9 L475.3,74.2 L483.8,77.3 L492.2,81.3 L500.7,86.1 L509.1,91.7 L517.6,97.8 L526.0,104.6 L534.4,111.8 L542.9,119.5 L551.3,127.5 L559.8,135.7 L568.2,144.1 L576.7,152.6 L585.1,161.1 L593.6,169.5 L602.0,177.8 L610.4,185.9 L618.9,193.7 L627.3,201.2 L635.8,208.4 L644.2,215.2 L652.7,221.6 L661.1,227.6 L669.6,233.2 L678.0,238.4 L686.4,243.2 L694.9,247.6 L703.3,251.6 L711.8,255.2 L720.2,258.4 L728.7,261.3 L737.1,263.9 L745.6,266.2 L754.0,268.2 L762.4,270.0 L770.9,271.5 L779.3,272.9 L787.8,274.0 L796.2,275.0 L804.7,275.8 L813.1,276.6 L821.6,277.2 L830.0,277.7";

const BELL_CURVE_TICKS = [
  { x: 196.7, label: "70" },
  { x: 323.3, label: "85" },
  { x: 450.0, label: "100" },
  { x: 576.7, label: "115" },
  { x: 703.3, label: "130" },
  { x: 830.0, label: "145" },
];

function BellCurveChart() {
  return (
    <div className="w-full">
      <svg
        viewBox="0 0 900 320"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={BELL_CURVE_FILL} fill="#2B2420" fillOpacity="0.05" />
        <path d={BELL_CURVE_SHADED} fill="#FF8C6B" fillOpacity="0.3" />
        <path
          d={BELL_CURVE_OUTLINE}
          fill="none"
          stroke="#2B2420"
          strokeOpacity="0.35"
          strokeWidth="2.5"
        />

        <line
          x1="70"
          y1="280"
          x2="830"
          y2="280"
          stroke="#2B2420"
          strokeOpacity="0.15"
          strokeWidth="2"
        />

        {BELL_CURVE_TICKS.map((tick) => (
          <g key={tick.label}>
            <line
              x1={tick.x}
              y1="280"
              x2={tick.x}
              y2="286"
              stroke="#2B2420"
              strokeOpacity="0.25"
              strokeWidth="2"
            />
            <text
              x={tick.x}
              y="306"
              textAnchor="middle"
              fontFamily="Manrope, sans-serif"
              fontWeight="600"
              fontSize="15"
              fill="#2B2420"
              fillOpacity="0.5"
            >
              {tick.label}
            </text>
          </g>
        ))}

        <line
          x1="450.0"
          y1="280"
          x2="450.0"
          y2="70.0"
          stroke="#2B2420"
          strokeOpacity="0.3"
          strokeWidth="2"
          strokeDasharray="5 5"
        />
        <text
          x="450.0"
          y="58"
          textAnchor="middle"
          fontFamily="Sora, sans-serif"
          fontWeight="700"
          fontSize="15"
          fill="#2B2420"
          fillOpacity="0.45"
        >
          Average · 100
        </text>

        <line
          x1="635.8"
          y1="280"
          x2="635.8"
          y2="208.4"
          stroke="#FF8C6B"
          strokeWidth="3"
        />
        <circle
          cx="635.8"
          cy="208.4"
          r="7"
          fill="#FF8C6B"
          stroke="#fff"
          strokeWidth="3"
        />

        <rect
          x="549.8"
          y="156.4"
          width="172"
          height="34"
          rx="17"
          fill="#2B2420"
        />
        <text
          x="635.8"
          y="179.4"
          textAnchor="middle"
          fontFamily="Sora, sans-serif"
          fontWeight="700"
          fontSize="15"
          fill="#FFF7F2"
        >
          You · 122 · 93rd
        </text>
      </svg>
    </div>
  );
}

/* ============================================================
   DEEP-DIVE SECTION ILLUSTRATIONS
   ============================================================ */

function IllustrationFrame({ background, children }) {
  return (
    <div
      className="w-full rounded-[20px] overflow-hidden"
      style={{
        aspectRatio: "4 / 3",
        background,
        boxShadow: "rgba(43, 36, 32, 0.05) 0px 0px 0px 1px inset",
      }}
    >
      <svg
        viewBox="0 0 400 300"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        {children}
      </svg>
    </div>
  );
}

function MatrixCell({ x, y, fillOpacity, radius, questionMark }) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width="58"
        height="58"
        rx="13"
        fill={questionMark ? "#ffffff" : "#8ECAE6"}
        fillOpacity={questionMark ? 1 : fillOpacity}
        stroke="#2B2420"
        strokeOpacity="0.14"
      />
      {questionMark ? (
        <text
          x={x + 29}
          y={y + 40}
          textAnchor="middle"
          fontFamily="Sora, sans-serif"
          fontWeight="800"
          fontSize="32"
          fill="#8ECAE6"
        >
          ?
        </text>
      ) : (
        <circle
          cx={x + 29}
          cy={y + 29}
          r={radius}
          fill="#2B2420"
          fillOpacity="0.6"
        />
      )}
    </g>
  );
}

function MatrixIllustration() {
  const cells = [
    { x: 100, y: 48, fillOpacity: 0.32, radius: 6 },
    { x: 174, y: 48, fillOpacity: 0.38, radius: 8.1 },
    { x: 248, y: 48, fillOpacity: 0.44, radius: 10.2 },
    { x: 100, y: 122, fillOpacity: 0.5, radius: 12.3 },
    { x: 174, y: 122, fillOpacity: 0.56, radius: 14.4 },
    { x: 248, y: 122, fillOpacity: 0.62, radius: 16.5 },
    { x: 100, y: 196, fillOpacity: 0.68, radius: 18.6 },
    { x: 174, y: 196, fillOpacity: 0.74, radius: 20.7 },
    { x: 248, y: 196, questionMark: true },
  ];

  return (
    <IllustrationFrame background="rgba(142, 202, 230, 0.15)">
      {cells.map((cell, i) => (
        <MatrixCell key={i} {...cell} />
      ))}
    </IllustrationFrame>
  );
}

function AnalogyBox({ x, letter, isAnswer }) {
  return (
    <g>
      <rect
        x={x}
        y="116"
        width="58"
        height="58"
        rx="14"
        fill={isAnswer ? "#ffffff" : "#C9A7FF"}
        fillOpacity={isAnswer ? 1 : 0.9}
        stroke="#2B2420"
        strokeOpacity="0.14"
      />
      <text
        x={x + 29}
        y="156"
        textAnchor="middle"
        fontFamily="Sora, sans-serif"
        fontWeight="800"
        fontSize="30"
        fill="#2B2420"
        fillOpacity="0.82"
      >
        {letter}
      </text>
    </g>
  );
}

function AnalogyColon({ x }) {
  return (
    <g>
      <circle cx={x} cy="138" r="4.5" fill="#2B2420" fillOpacity="0.4" />
      <circle cx={x} cy="156" r="4.5" fill="#2B2420" fillOpacity="0.4" />
    </g>
  );
}

function AnalogyIllustration() {
  return (
    <IllustrationFrame background="rgba(201, 167, 255, 0.15)">
      <AnalogyBox x={24} letter="A" />
      <AnalogyColon x={102} />
      <AnalogyBox x={116} letter="B" />
      <AnalogyColon x={200} />
      <AnalogyColon x={214} />
      <AnalogyBox x={232} letter="C" />
      <AnalogyColon x={310} />
      <AnalogyBox x={322} letter="?" isAnswer />
    </IllustrationFrame>
  );
}

function DataPoint({ x, y, label }) {
  return (
    <g>
      <circle
        cx={x}
        cy={y}
        r="7"
        fill="#ffffff"
        stroke="#2B2420"
        strokeWidth="3"
        strokeOpacity="0.7"
      />
      <text
        x={x}
        y={y - 16}
        textAnchor="middle"
        fontFamily="Sora, sans-serif"
        fontWeight="700"
        fontSize="17"
        fill="#2B2420"
        fillOpacity="0.65"
      >
        {label}
      </text>
    </g>
  );
}

function GrowthCurveIllustration() {
  const points = [
    { x: 52, y: 244.9, label: "2" },
    { x: 126, y: 218.8, label: "6" },
    { x: 200, y: 179.6, label: "12" },
    { x: 274, y: 127.3, label: "20" },
    { x: 348, y: 62.0, label: "30" },
  ];

  const linePoints = points.map((p) => `${p.x},${p.y}`).join(" ");
  const areaPath = `M52,258 L${points
    .map((p) => `${p.x},${p.y}`)
    .join(" L")} L348,258 Z`;

  return (
    <IllustrationFrame background="rgba(255, 140, 107, 0.15)">
      <line
        x1="40"
        y1="258"
        x2="360"
        y2="258"
        stroke="#2B2420"
        strokeOpacity="0.12"
        strokeWidth="2"
      />
      <path d={areaPath} fill="#FF8C6B" fillOpacity="0.2" />
      <polyline
        points={linePoints}
        fill="none"
        stroke="#FF8C6B"
        strokeWidth="4.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map((p) => (
        <DataPoint key={p.label} {...p} />
      ))}
    </IllustrationFrame>
  );
}

function RotationIllustration() {
  return (
    <IllustrationFrame background="rgba(157, 227, 208, 0.15)">
      <rect
        x="140"
        y="92"
        width="120"
        height="120"
        rx="16"
        fill="none"
        stroke="#2B2420"
        strokeOpacity="0.22"
        strokeWidth="2.5"
        strokeDasharray="7 7"
      />
      <g transform="rotate(38 200 152)">
        <rect
          x="140"
          y="92"
          width="120"
          height="120"
          rx="16"
          fill="#9DE3D0"
          fillOpacity="0.85"
          stroke="#2B2420"
          strokeOpacity="0.14"
        />
        <circle cx="200" cy="112" r="11" fill="#2B2420" fillOpacity="0.6" />
      </g>
      <path
        d="M116 92 A96 96 0 0 1 250 60"
        fill="none"
        stroke="#2B2420"
        strokeOpacity="0.5"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      <path d="M250 60 l-15 -4 l6 15 z" fill="#2B2420" fillOpacity="0.5" />
    </IllustrationFrame>
  );
}

function VennCircle({ cx, fillOpacity }) {
  return (
    <circle
      cx={cx}
      cy="118"
      r="70"
      fill="#FFD166"
      fillOpacity={fillOpacity}
      stroke="#2B2420"
      strokeOpacity="0.1"
    />
  );
}

function VennLabel({ x, children }) {
  return (
    <text
      x={x}
      y="128"
      textAnchor="middle"
      fontFamily="Sora, sans-serif"
      fontWeight="800"
      fontSize="30"
      fill="#2B2420"
      fillOpacity="0.7"
    >
      {children}
    </text>
  );
}

function SyllogismIllustration() {
  return (
    <IllustrationFrame background="rgba(255, 209, 102, 0.15)">
      <VennCircle cx={166} fillOpacity={0.34} />
      <VennCircle cx={234} fillOpacity={0.55} />
      <VennLabel x={130}>P</VennLabel>
      <VennLabel x={270}>Q</VennLabel>

      <line
        x1="200"
        y1="192"
        x2="200"
        y2="228"
        stroke="#2B2420"
        strokeOpacity="0.35"
        strokeWidth="3.5"
      />
      <path d="M200 232 l-6 -13 l12 0 z" fill="#2B2420" fillOpacity="0.35" />

      <rect x="150" y="232" width="100" height="44" rx="22" fill="#2B2420" />
      <text
        x="200"
        y="262"
        textAnchor="middle"
        fontFamily="Sora, sans-serif"
        fontWeight="800"
        fontSize="26"
        fill="#FFF7F2"
      >
        ∴
      </text>
    </IllustrationFrame>
  );
}

/* ============================================================
   DEEP-DIVE SECTION (shared layout for all 5 sections)
   ============================================================ */

function StepMarker({ number, color }) {
  return (
    <div className="flex items-center gap-4 mb-5">
      <span className={`text-3xl font-extrabold ${color.text}`}>{number}</span>
      <span className={`h-0.5 w-14 ${color.bg}`} />
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-4xl font-extrabold text-neutral-900 mb-5">
      {children}
    </h2>
  );
}

function DeepDiveSection({ section }) {
  const {
    number,
    name,
    description,
    body,
    score,
    total,
    percent,
    color,
    strength,
    improve,
    illustration,
    illustrationSide,
  } = section;

  const textColumn = (
    <div>
      <StepMarker number={number} color={color} />
      <SectionTitle>{name}</SectionTitle>
      <Description className="mb-5">{description}</Description>
      <Description className="mb-8">{body}</Description>
      <ScoreBar
        label="Section score"
        score={score}
        total={total}
        percent={percent}
        barColor={color.bg}
      />
      <InsightRow strength={strength} improve={improve} />
    </div>
  );

  const illustrationColumn = (
    <div className="self-start lg:mt-14">{illustration}</div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
      {illustrationSide === "left" ? (
        <>
          {illustrationColumn}
          {textColumn}
        </>
      ) : (
        <>
          {textColumn}
          {illustrationColumn}
        </>
      )}
    </div>
  );
}

/* ============================================================
   RETAKE / SHARE CTA
   ============================================================ */

function CtaTitle({ children }) {
  return (
    <h2 className="text-5xl font-extrabold text-neutral-900 leading-tight text-center max-w-2xl mx-auto mb-6">
      {children}
    </h2>
  );
}

function CtaDescription({ children }) {
  return (
    <p className="text-xl text-neutral-600 text-center max-w-lg mx-auto mb-9">
      {children}
    </p>
  );
}

function PrimaryButton({ children }) {
  return (
    <button className="bg-neutral-900 text-white font-bold text-lg px-7 py-4 rounded-full hover:bg-neutral-800 transition-colors">
      {children}
    </button>
  );
}

function SecondaryButton({ children }) {
  return (
    <button className="border border-neutral-900/30 text-neutral-900 font-bold text-lg px-7 py-4 rounded-full hover:bg-white/30 transition-colors">
      {children}
    </button>
  );
}

function ButtonRow({ children }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4">
      {children}
    </div>
  );
}

function RetakeCta() {
  return (
    <div className="relative w-full max-w-6xl mx-auto bg-sky-300 rounded-[28px] overflow-hidden px-8 py-16 md:px-16">
      <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-sky-200/50" />

      <div className="relative">
        <CtaTitle>Want to push your score higher?</CtaTitle>
        <CtaDescription>
          Retake the assessment, or share how you did with friends.
        </CtaDescription>
        <ButtonRow>
          <PrimaryButton>Retake the test →</PrimaryButton>
          <SecondaryButton>Share your results</SecondaryButton>
        </ButtonRow>
      </div>
    </div>
  );
}

/* ============================================================
   PAGE
   ============================================================ */

const IQReport = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const assessmentId = queryParams.get("assessmentId");
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;

  return (
    <>
      <Header />
      <div className="min-h-screen  px-8 py-14 md:px-16 space-y-20">
        {/* Hero */}
        <div className="max-w-6xl mx-auto">
          <Eyebrow>Your Results · Jordan Iversen</Eyebrow>
          <Title>
            Cognitive Assessment
            <br />
            Report
          </Title>
          <Subtitle>
            A strong, well-rounded performance. Spatial Reasoning was your
            standout area, while Numerical Reasoning is where a little practice
            would lift your score the most.
          </Subtitle>

          <div className="flex flex-col md:flex-row gap-6">
            <IQCard
              iq={122}
              rating="Superior"
              percentile={93}
              correct={64}
              total={85}
              accuracy={75}
              consistency={85}
            />
            <SectionScoresCard sections={SECTIONS} />
          </div>
        </div>

        {/* Where you rank */}
        <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-sm p-8 md:p-12">
          <CardHeader
            eyebrow="How you compare"
            title="Where you rank"
            description="Your score sits well to the right of the average. The shaded region is the share of test-takers you scored above."
          />
          <BellCurveChart />
          <Legend />
        </div>

        {/* Deep dive */}
        <div className="max-w-6xl mx-auto">
          <Eyebrow>Deep dive</Eyebrow>
          <h1 className="text-5xl font-extrabold text-neutral-900 leading-tight mb-14">
            How you did, section by section
          </h1>

          <div className="space-y-20">
            {SECTIONS.map((section) => (
              <DeepDiveSection key={section.number} section={section} />
            ))}
          </div>
        </div>

        {/* CTA */}
        <RetakeCta />
        <ReportWrapper
          assessmentId={assessmentId}
          assessmentAttemptId={assessmentAttemptId}
          currentLocale={currentLocale}
        />
      </div>
      <Footer />
    </>
  );
};

export default IQReport;
